/**
 * Authentication Service
 * ============================================================================
 *
 * Purpose:
 * --------
 * Authentication service functions using functional programming principles.
 * Provides API integration for authentication operations.
 *
 * This service provides:
 * - Login/logout/register operations
 * - Token refresh
 * - Password management
 * - User profile operations
 * - OAuth integration
 * - Event emission
 *
 * Design Principles:
 * ------------------
 * - **Functional programming** (no classes!)
 * - Pure functions where possible
 * - API integration
 * - Error transformation
 * - Event-driven architecture
 *
 * Usage:
 * ------
 * ```typescript
 * import { login, logout, getCurrentUser } from 'core/auth';
 *
 * const response = await login({ email, password });
 * ```
 * ============================================================================
 */

import type {
  AuthEventHandler,
  AuthEventType,
  ChangePasswordData,
  ForgotPasswordData,
  LoginCredentials,
  LoginResponse,
  OAuthLoginData,
  RegisterData,
  RegisterResponse,
  ResetPasswordData,
  TokenRefreshResponse,
  UpdateProfileData,
  User,
  VerifyEmailData,
} from './types';

import { AUTH_ENDPOINTS } from './constants';
import { apiClient } from '@/api/config';

/**
 * Event listeners
 *
 * Using Map for event management (functional approach).
 */
const eventListeners = new Map<AuthEventType, Set<AuthEventHandler>>();

/**
 * Subscribe to auth event
 *
 * @param event - Event type
 * @param handler - Event handler
 * @returns Unsubscribe function
 */
export const onAuthEvent = (event: AuthEventType, handler: AuthEventHandler): (() => void) => {
  if (!eventListeners.has(event)) {
    eventListeners.set(event, new Set());
  }

  eventListeners.get(event)!.add(handler);

  // Return unsubscribe function
  return () => {
    eventListeners.get(event)?.delete(handler);
  };
};

/**
 * Emit auth event
 *
 * @param event - Event type
 * @param data - Event data
 */
export const emitAuthEvent = (event: AuthEventType, data?: unknown): void => {
  const handlers = eventListeners.get(event);
  if (handlers) {
    handlers.forEach((handler) => handler(data));
  }
};

/**
 * Login with credentials
 *
 * @param credentials - Login credentials
 * @returns Login response
 */
export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>(AUTH_ENDPOINTS.LOGIN, credentials);
  return response.data;
};

/**
 * Logout
 *
 * Calls logout endpoint to invalidate refresh token on server.
 */
export const logout = async (): Promise<void> => {
  try {
    await apiClient.post(AUTH_ENDPOINTS.LOGOUT);
  } catch (error) {
    // Log error but don't throw - we still want to clear local state
    console.warn('Logout API call failed:', error);
  }
};

/**
 * Register new user
 *
 * @param data - Registration data
 * @returns Register response
 */
export const register = async (data: RegisterData): Promise<RegisterResponse> => {
  const response = await apiClient.post<RegisterResponse>(AUTH_ENDPOINTS.REGISTER, data);
  return response.data;
};

/**
 * Refresh access token
 *
 * @param refreshToken - Refresh token
 * @returns Token refresh response
 */
export const refreshToken = async (refreshToken: string): Promise<TokenRefreshResponse> => {
  const response = await apiClient.post<TokenRefreshResponse>(AUTH_ENDPOINTS.REFRESH, {
    refreshToken,
  });
  return response.data;
};

/**
 * Get current user
 *
 * Fetches authenticated user data.
 *
 * @returns User data
 */
export const getCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get<User>(AUTH_ENDPOINTS.ME);
  return response.data;
};

/**
 * Change password
 *
 * @param data - Password change data
 */
export const changePassword = async (data: ChangePasswordData): Promise<void> => {
  await apiClient.post(AUTH_ENDPOINTS.CHANGE_PASSWORD, data);
};

/**
 * Request password reset
 *
 * @param data - Forgot password data
 */
export const forgotPassword = async (data: ForgotPasswordData): Promise<void> => {
  await apiClient.post(AUTH_ENDPOINTS.FORGOT_PASSWORD, data);
};

/**
 * Reset password with token
 *
 * @param data - Reset password data
 */
export const resetPassword = async (data: ResetPasswordData): Promise<void> => {
  await apiClient.post(AUTH_ENDPOINTS.RESET_PASSWORD, data);
};

/**
 * Verify email with OTP code
 *
 * Uses the onboarding endpoint for email verification.
 *
 * @param data - Email and OTP code
 */
export const verifyEmail = async (data: VerifyEmailData): Promise<void> => {
  await apiClient.post('/onboarding/verify-email', data);
};

/**
 * Resend verification OTP
 *
 * @param email - Email address to resend OTP to
 */
export const resendVerificationOtp = async (email: string): Promise<void> => {
  await apiClient.post('/onboarding/resend-verification', { email });
};

/**
 * Update user profile
 *
 * @param data - Profile update data
 * @returns Updated user data
 */
export const updateProfile = async (data: UpdateProfileData): Promise<User> => {
  const response = await apiClient.put<User>(AUTH_ENDPOINTS.UPDATE_PROFILE, data);
  return response.data;
};

/**
 * OAuth login
 *
 * @param data - OAuth login data
 * @returns Login response
 */
export const loginWithOAuth = async (data: OAuthLoginData): Promise<LoginResponse> => {
  const endpoint = AUTH_ENDPOINTS.OAUTH_CALLBACK.replace(':provider', data.provider);
  const response = await apiClient.post<LoginResponse>(endpoint, {
    code: data.code,
    state: data.state,
    redirectUri: data.redirectUri,
  });
  return response.data;
};

/**
 * Initiate OAuth login
 *
 * Returns OAuth authorization URL.
 *
 * @param provider - OAuth provider
 * @param redirectUri - Redirect URI
 * @returns Authorization URL
 */
export const getOAuthUrl = async (
  provider: string,
  redirectUri?: string
): Promise<string> => {
  const endpoint = AUTH_ENDPOINTS.OAUTH_LOGIN.replace(':provider', provider);
  const response = await apiClient.get<{ url: string }>(endpoint, {
    params: { redirectUri },
  });
  return response.data.url;
};
